import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import BookingButton from './BookingButton';

const { FiAlertCircle, FiClock, FiArrowRight } = FiIcons;

const ProblemSection = () => {
  const [ref, inView] = useInView({ triggerOnce: true, threshold: 0.1 });

  const problems = [
    "Your 2-year-old has fewer than 50 words while friends' kids are chatting away",
    "Mealtimes are battles—They gag on textures, eat only 3 \"safe\" foods, and you're terrified they aren't getting enough nutrition",
    "Other parents keep asking \"Is everything okay?\" when they see your child's behaviors",
    "You've heard \"boys talk later\" or \"they'll catch up\" but your instinct says differently",
    "Simple transitions trigger massive meltdowns that last for hours",
    "Your child seems \"in their own world\" more than connecting with others",
    "Everyone has advice, but no one has real answers"
  ];

  const keyFacts = [
    "25% of all children and as many as 80% of children with developmental disabilities experience feeding disorders¹",
    "The presence of speech before five years of age is increasingly important for long-term outcomes²",
    "As many as 72% of children with Fetal Alcohol Syndrome also meet criteria for autism³"
  ];

  return (
    <section id="problem" className="py-20 bg-white" ref={ref}>
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl lg:text-5xl font-bold text-gray-900 mb-6 font-serif">
            When Your Parent Gut Says Something's Not Quite <span className="gradient-text">Right</span>
          </h2>
          <p className="text-xl text-gray-600 font-serif italic">
            The Quiet Worry of "What If?"
          </p>
        </motion.div>

        <div className="space-y-16">
          {/* Sound Familiar Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8 font-serif">Sound familiar?</h3>
            <ul className="space-y-4">
              {problems.map((problem, index) => (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -20 }}
                  animate={inView ? { opacity: 1, x: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-start space-x-3"
                >
                  <SafeIcon icon={FiAlertCircle} className="h-5 w-5 text-accent-magenta mt-1 flex-shrink-0" />
                  <span className="text-gray-700 leading-relaxed">{problem}</span>
                </motion.li>
              ))}
            </ul>
          </motion.div>

          {/* The Early Years Reality Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="bg-gradient-to-br from-gray-50 to-accent-light/20 p-8 rounded-2xl"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-8 flex items-center font-serif">
              <SafeIcon icon={FiClock} className="h-6 w-6 text-accent-magenta mr-3" />
              The Early Years Reality
            </h3>
            <div className="flex flex-col space-y-3">
              <div className="flex items-center">
                <div className="bg-white rounded-lg p-4 shadow-sm flex-1 text-center">
                  <p className="font-semibold text-gray-900">Critical brain development</p>
                </div>
                <SafeIcon icon={FiArrowRight} className="h-5 w-5 text-gray-400 mx-3" />
              </div>
              <div className="flex items-center">
                <div className="bg-white rounded-lg p-4 shadow-sm flex-1 text-center">
                  <p className="font-semibold text-gray-900">Missed milestones</p>
                </div>
                <SafeIcon icon={FiArrowRight} className="h-5 w-5 text-gray-400 mx-3" />
              </div>
              <div className="flex items-center">
                <div className="bg-white rounded-lg p-4 shadow-sm flex-1 text-center">
                  <p className="font-semibold text-gray-900">Growing gaps</p>
                </div>
                <SafeIcon icon={FiArrowRight} className="h-5 w-5 text-gray-400 mx-3" />
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm text-center">
                <p className="font-semibold text-gray-900">Harder to close later</p>
              </div>
            </div>
          </motion.div>

          {/* Truth Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="bg-gradient-to-r from-accent-purple/10 to-accent-magenta/10 p-8 rounded-2xl border border-accent-purple/20"
          >
            <p className="text-gray-800 mb-4 leading-relaxed">
              The truth is, you're not overreacting. An estimated <span className="font-bold text-accent-magenta">1 in 6 U.S. children—approximately 16%</span>—has a developmental delay, disorder, or disability, but less than half are identified.
            </p>
            <p className="text-gray-800 leading-relaxed">
              Early differences in communication, behavior, and executive function can signal developing brains that need specialized support.
            </p>
          </motion.div>

          {/* Key Facts Section */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100"
          >
            <h3 className="text-2xl font-bold text-gray-900 mb-6 font-serif">Key Facts Every Parent Should Know:</h3>
            <div className="space-y-4">
              {keyFacts.map((fact, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.9 + index * 0.2 }}
                  className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-accent-magenta"
                >
                  <p className="text-gray-700 leading-relaxed">{fact}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>

          {/* CTA Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 1.2 }}
            className="text-center"
          >
            <BookingButton text="Get Help Now" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;